<template>
  <div class="tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-p-2">
    <!-- Compact Input Area -->
    <div class="tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm focus-within:tw-ring-2 focus-within:tw-ring-primary-500 focus-within:tw-border-primary-500 tw-transition-all">
      <!-- Textarea -->
      <textarea 
        v-model="messageText"
        @keydown.enter.prevent="sendMessage"
        @input="handleInput"
        placeholder="Type your message..."
        class="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border-0 tw-rounded-lg tw-resize-none tw-outline-none tw-bg-transparent tw-min-h-[8px] tw-max-h-[80px]"
        rows="1"
        ref="messageInput"
        :disabled="sending"
      ></textarea>
      
      <!-- Input Actions -->
      <div class="tw-absolute tw-right-1 tw-top-1 tw-flex tw-items-center tw-gap-1">
        
        <!-- Send Button -->
        <button
          @click="sendMessage"
          :disabled="!messageText.trim() || sending"
          class="tw-p-2 tw-text-white tw-bg-primary-600 hover:tw-bg-primary-700 disabled:tw-bg-gray-300 disabled:tw-cursor-not-allowed tw-rounded-lg tw-transition-colors"
          title="Send message"
        >
          <icon 
            v-if="sending" 
            icon="line-md:loading-twotone-loop" 
            class="tw-text-sm tw-animate-spin" 
          />
          <icon 
            v-else 
            :icon="messageText.trim() ? 'solar:map-arrow-right-bold-duotone' : 'solar:map-arrow-right-linear'" 
            class="tw-text-sm" 
          />
        </button>
      </div>
    </div>

    <!-- Compact Quick Reply Suggestions -->
    <div v-if="!messageText.trim() && !sending" class="tw-flex tw-items-center tw-gap-1 tw-mt-2 tw-flex-wrap">
      <button 
        v-for="suggestion in sessionQuickReplies.slice(0, 4)" 
        :key="suggestion"
        @click="useQuickReply(suggestion)"
        class="tw-px-2 tw-py-0.5 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-200 tw-transition-all tw-duration-200 tw-text-gray-600 hover:tw-text-primary-700"
      >
        {{ suggestion }}
      </button>
    </div>

    <!-- File Upload Input (Hidden) -->
    <input
      ref="fileInput"
      type="file"
      multiple
      accept="image/*,.pdf,.doc,.docx,.txt"
      class="tw-hidden"
      @change="handleFileSelect"
    />
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, nextTick, onUnmounted, computed } from 'vue';
import { useAuthStore } from '@/stores';

// Props
defineProps({
  sessionId: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

// Note: realtimeMessagingService import removed as it's not currently used
// Can be re-imported when real-time typing indicators are implemented

// Emits
const emit = defineEmits(['message-sent', 'message-sending']);

// Get auth store
const authStore = useAuthStore();
const currentUser = computed(() => authStore.user);

// Reactive data
const messageText = ref('');
const sending = ref(false);
const messageInput = ref(null);
const fileInput = ref(null);
const typingTimeout = ref(null);

// Session-specific quick reply suggestions
const sessionQuickReplies = ref([
  'Great session!',
  'I have a question',
  'Thank you for sharing',
  'This is very helpful',
  'Can you elaborate on that?',
  'I agree with that point',
  'Interesting perspective',
  'Looking forward to more'
]);

// Auto-resize textarea
const handleInput = () => {
  if (messageInput.value) {
    messageInput.value.style.height = 'auto';
    messageInput.value.style.height = Math.min(messageInput.value.scrollHeight, 120) + 'px';
  }
  
  // Handle typing indicators
  handleTyping();
};

// Handle typing indicators
const handleTyping = () => {
  // Clear existing timeout
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
  }
  
  // Emit typing started
  emitTypingEvent('typing-started');
  
  // Set timeout to stop typing after 2 seconds of inactivity
  typingTimeout.value = setTimeout(() => {
    emitTypingEvent('typing-stopped');
  }, 2000);
};

// Emit typing events
const emitTypingEvent = (eventType) => {
  try {
    // This would typically emit to the real-time service
    // For now, we'll just log it
    console.log(`Session typing event: ${eventType}`);
  } catch (err) {
    console.error('Error emitting typing event:', err);
  }
};

// Send message
const sendMessage = async () => {
  if (!messageText.value.trim() || sending.value) return;
  
  const messageContent = messageText.value.trim();
  sending.value = true;
  
  try {
    // Emit message sending event
    emit('message-sending', true);
    
    // Create message object with actual current user data
    const message = {
      id: `temp-${Date.now()}`, // Temporary ID that starts with 'temp-'
      content: messageContent,
      sender_id: currentUser.value?.id || 'temp-user', // Use actual user ID
      sender: {
        id: currentUser.value?.id,
        name: currentUser.value?.name || 'You',
        profile_image: currentUser.value?.profile_image || null
      },
      created_at: new Date().toISOString(),
      type: 'text'
    };
    
    // Emit message sent event (for API handling in parent)
    emit('message-sent', message);
    
    // Clear input
    messageText.value = '';
    
    // Reset textarea height
    if (messageInput.value) {
      messageInput.value.style.height = 'auto';
    }
    
    // Stop typing indicator
    if (typingTimeout.value) {
      clearTimeout(typingTimeout.value);
      typingTimeout.value = null;
    }
    emitTypingEvent('typing-stopped');
    
    // Focus back to input
    nextTick(() => {
      if (messageInput.value) {
        messageInput.value.focus();
      }
    });
    
  } catch (err) {
    console.error('Error sending message:', err);
  } finally {
    sending.value = false;
    emit('message-sending', false);
  }
};

// Use quick reply
const useQuickReply = (suggestion) => {
  messageText.value = suggestion;
  if (messageInput.value) {
    messageInput.value.focus();
    messageInput.value.style.height = 'auto';
    messageInput.value.style.height = Math.min(messageInput.value.scrollHeight, 120) + 'px';
  }
};


const handleFileSelect = (event) => {
  const files = Array.from(event.target.files);
  
  if (files.length > 0) {
    // Handle file upload logic here
    console.log('Files selected:', files);
    
    // For now, we'll just show a placeholder message
    messageText.value = `[Attached ${files.length} file${files.length !== 1 ? 's' : ''}]`;
    
    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};


// Cleanup
onUnmounted(() => {
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
  }
});
</script>

<style scoped>
</style>
